import React from 'react';

import { isEmpty, uniqBy } from 'lodash/fp';
import { parse } from 'papaparse';

import _ from '../../locale';
import { OrganizationMemberCSV } from '../../types';
import { parseApiError as commonParseApiError } from '../../utility/api-helpers';
import { validateEmail } from '../../utility/validation';
import BulkInviteAccountView from './BulkInviteAccountView';

type Props = {
    organizationId: number;
    isVisible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bulkCreateOrganizationMembers: (organizationId, members) => Promise<object | object[]>;
};

type State = {
    newMembers: OrganizationMemberCSV[];
    errors: { fields: any } | null;
    isFileSelected: boolean;
    isSubmittable: boolean;
    isSubmitting: boolean;
    successMessage: string | null;
};

const INITIAL_STATE = {
    newMembers: [],
    errors: null,
    isFileSelected: false,
    isSubmittable: false,
    isSubmitting: false,
    successMessage: null,
};

class BulkInviteAccountModal extends React.Component<Props, State> {
    state: State = INITIAL_STATE;

    onUploadFile = (event) => {
        const input = event.target;
        if (!isEmpty(input?.files)) {
            this.setState({
                isFileSelected: true,
                isSubmittable: true,
                successMessage: null,
            });
            this.readFile(input.files[0]);
        }
    };

    readFile = (file) => {
        const csvRegExp = RegExp('.+(.csv)$');
        if (!csvRegExp.test(file.name)) {
            this.setState({
                errors: {
                    fields: [_('error_only_csv')],
                },
            });
            return;
        }

        parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                this.setState({
                    isSubmittable: true,
                });

                return results.errors && results.errors.length
                    ? this.onError(results.errors)
                    : this.onData(results.data);
            },
            error: this.onError,
        });
    };

    onData = (data: object[]) => {
        const getMemberPropertyValue = (
            member: object,
            fields: string[],
            defaultValue: string = ''
        ): string => {
            let value = null;
            fields.forEach((field) => {
                if (member[field]) {
                    value = member[field];
                }
            });
            return (value || defaultValue).trim();
        };

        const unifiedMembersSourceList: OrganizationMemberCSV[] = data.map((member) => ({
            email: getMemberPropertyValue(member, [
                'email',
                'E-mail 1 - Value',
                'E-mail 2 - Value',
            ]),
            first_name: getMemberPropertyValue(member, ['firstname', 'Given Name']),
            last_name: getMemberPropertyValue(member, ['lastname', 'Family Name']),
            title: getMemberPropertyValue(member, ['title', 'Occupation']),
        }));

        const newMembers = uniqBy('email', unifiedMembersSourceList).filter((i) =>
            validateEmail(i.email)
        );

        const emails = newMembers.map((i) => i.email);
        const errors = emails && emails.length ? null : { fields: _('error_no_valid_emails') };

        this.setState({
            newMembers,
            errors,
            isSubmittable: !errors,
        });
    };

    onError = (event) => {
        const errors = { fields: _('error_not_valid_csv_file') };
        this.setState({ errors, isSubmittable: false, isSubmitting: false });
    };

    onClose = () => {
        this.setState(INITIAL_STATE);
        this.props.onClose();
    };

    onSuccess = (responses) => {
        const errors = responses
            .map((response, idx) => ({
                ...response,
                idx,
            }))
            .filter(this.hasError)
            .map(this.parseApiError);

        if (isEmpty(errors)) {
            this.setState(INITIAL_STATE);
            this.props.onSuccess();
            return;
        }

        const successInvitesAmount = responses.filter(
            (response) => !this.hasError(response)
        ).length;

        this.setState({
            isSubmittable: false,
            isSubmitting: false,
            errors,
            successMessage: successInvitesAmount
                ? _('bulk_invite_successfully_created_accounts', { amount: successInvitesAmount })
                : null,
        });
    };

    parseApiError = ({ error, idx }) => {
        const commonParsedError = commonParseApiError(error);

        const { email, first_name, last_name } = this.state.newMembers[idx];

        const fullName = first_name && last_name ? `${first_name || ''} ${last_name || ''}` : null;
        const rowNumber = `Row #${idx + 1}`;

        const prefix = email || fullName || rowNumber;
        return prefix + ': ' + Object.values(commonParsedError).join('; ');
    };

    hasError = (response): boolean => {
        return response.hasOwnProperty('error');
    };

    onInvite = () => {
        const { bulkCreateOrganizationMembers, organizationId } = this.props;
        const { isSubmittable, newMembers } = this.state;

        if (!isSubmittable) {
            return;
        }

        this.setState({ isSubmittable: false, isSubmitting: true });

        bulkCreateOrganizationMembers(organizationId, newMembers)
            .then(this.onSuccess)
            .catch((response) => {
                this.setState({
                    isSubmittable: false,
                    isSubmitting: false,
                    errors: commonParseApiError(response),
                });
            });
    };

    render() {
        const { isVisible } = this.props;
        const { errors, isFileSelected, isSubmittable, isSubmitting, newMembers, successMessage } =
            this.state;

        return (
            <BulkInviteAccountView
                isVisible={isVisible}
                emails={newMembers.map((i) => i.email)}
                onInvite={this.onInvite}
                onClose={this.onClose}
                onUploadFile={this.onUploadFile}
                errors={errors}
                isFileSelected={isFileSelected}
                isSubmittable={isSubmittable}
                isSubmitting={isSubmitting}
                successMessage={successMessage}
            />
        );
    }
}

export default BulkInviteAccountModal;
